import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Form, Formik } from "formik";
import { memo, useCallback, useRef } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    usePatchOrganizationClientMutation,
    useUpdateClientEntityMutation,
} from "@api/organizationClient.api";
import { useScope } from "@state/scopeContext.lib";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import {
    NoteFormSchema,
    useNoteEditing,
} from "../../clients/views/cells/useNoteEditing";
import { NoteTextarea } from "../../clients/views/cells/NoteTextarea";
import { ClientsCloseOverviewRow } from "../clientsCloseOverviewViewConfig";
import styles from "../../clients/views/cells/NoteCell.module.scss";

export const CloseNoteCell = memo(
    ({
        row,
        getValue,
    }: CellContext<
        ViewTableFeatures,
        ClientsCloseOverviewRow,
        string | null | undefined
    >) => {
        const { organizationId } = useScope();
        const { mutate: updateEntityMutate } = useUpdateClientEntityMutation();
        const { mutate: patchClientMutate } =
            usePatchOrganizationClientMutation();

        const note = getValue();
        const { rowType, workspaceId, entityId, id } = row.original;

        const handleUpdate = useCallback(
            (payload: string) => {
                if (!organizationId) {
                    return;
                }

                if (rowType === "entity") {
                    if (!workspaceId || !entityId) {
                        return;
                    }
                    updateEntityMutate({
                        organizationId,
                        clientId: workspaceId,
                        entityId,
                        payload: { note: payload },
                    });
                } else if (rowType === "workspace") {
                    const clientId = id.replace("workspace:", "");
                    patchClientMutate({
                        organizationId,
                        clientId,
                        payload: { note: payload },
                    });
                }
            },
            [
                organizationId,
                rowType,
                workspaceId,
                entityId,
                id,
                updateEntityMutate,
                patchClientMutate,
            ],
        );

        // Stable ref-backed close callback so useNoteEditing can close the
        // controlled Popover without circular dependency on setIsOpened.
        const closeRef = useRef<() => void>(() => undefined);
        const closePopover = useCallback(() => closeRef.current(), []);

        const {
            isOpened,
            setIsOpened,
            textareaRef,
            formRef,
            handleSubmit,
            handleBlur,
            handleFocus,
            handleKeyDown,
        } = useNoteEditing(note, handleUpdate, closePopover);

        // Wire the ref now that setIsOpened is available
        closeRef.current = () => setIsOpened(false);

        return (
            <ViewTableCell captureClicks className={styles.container}>
                <Formik<NoteFormSchema>
                    initialValues={{ value: note ?? "" }}
                    onSubmit={handleSubmit}
                    enableReinitialize
                    innerRef={formRef}
                >
                    <Popover open={isOpened} onOpenChange={setIsOpened}>
                        <PopoverTrigger>
                            <EditableCellContent
                                isEmpty={!note}
                                truncationFullText={note ?? undefined}
                            >
                                {note}
                            </EditableCellContent>
                        </PopoverTrigger>
                        <PopoverContent
                            side="bottom"
                            align="start"
                            sideOffset={-32}
                            className={styles.popover}
                        >
                            <Form>
                                <NoteTextarea
                                    textareaRef={textareaRef}
                                    handleFocus={handleFocus}
                                    handleBlur={handleBlur}
                                    handleKeyDown={handleKeyDown}
                                    noteTextareaClass={styles.noteTextarea}
                                />
                            </Form>
                        </PopoverContent>
                    </Popover>
                </Formik>
            </ViewTableCell>
        );
    },
);

CloseNoteCell.displayName = "CloseNoteCell";
